/**
 * Executive Morning Briefing
 * 
 * The "front door" of Aligned for executives.
 * Every executive should begin the day here.
 * 
 * Features:
 * - Personalized greeting based on time of day
 * - Today's Operations snapshot
 * - Operational Readiness with explanation
 * - Suggested Actions (priority-ordered)
 * - Daily Learning report
 * - Ask Leynix integration
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Calendar, Users, AlertCircle, UserPlus, Clipboard, FileText,
  DollarSign, Receipt, ShieldAlert, Activity, ArrowRight, Sparkles,
  CheckCircle, AlertTriangle, Clock, Loader2, RefreshCw, Send,
  TrendingUp, Bell, Heart
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

// Icon mapping for metrics
const METRIC_ICONS = {
  calendar: Calendar,
  users: Users,
  "alert-circle": AlertCircle,
  "user-plus": UserPlus,
  clipboard: Clipboard,
  "file-text": FileText,
  "dollar-sign": DollarSign,
  receipt: Receipt,
  "shield-alert": ShieldAlert,
  activity: Activity,
  heart: Heart,
};

// Metric card component with enterprise styling
const MetricCard = ({ metric, onClick }) => {
  const Icon = METRIC_ICONS[metric.icon] || Activity;
  const hasAlert = metric.alert;
  
  return (
    <button
      onClick={onClick}
      className={`bg-white rounded border p-4 text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${
        hasAlert ? 'border-l-4 border-l-amber-500 border-amber-200' : 'border-[#E2E8F0]'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded flex items-center justify-center ${
          hasAlert ? 'bg-amber-50' : 'bg-[#F1F5F9]'
        }`}>
          <Icon className={`w-5 h-5 ${hasAlert ? 'text-amber-600' : 'text-[#0A192F]'}`} />
        </div>
        {hasAlert && (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Action Required</Badge>
        )}
      </div>
      <p className={`text-3xl font-bold ${metric.is_percentage ? 'text-emerald-600' : 'text-[#0A192F]'}`}>
        {metric.value}{metric.is_percentage ? '%' : ''}
      </p>
      <p className="text-sm font-medium text-[#0A192F]">{metric.label}</p>
      <p className="text-xs text-[#475569]">{metric.detail}</p>
    </button>
  );
};

// Suggested action card with enterprise styling
const ActionCard = ({ action }) => {
  const navigate = useNavigate();
  const Icon = METRIC_ICONS[action.icon] || ArrowRight;
  
  const priorityStyles = {
    critical: "border-l-4 border-l-red-500 bg-red-50/50",
    high: "border-l-4 border-l-orange-500 bg-orange-50/50",
    medium: "border-l-4 border-l-amber-500 bg-amber-50/50",
    low: "border-l-4 border-l-slate-300 bg-slate-50/50",
  };
  
  const priorityText = {
    critical: "text-red-800",
    high: "text-orange-800",
    medium: "text-amber-800",
    low: "text-slate-600",
  };
  
  return (
    <button
      onClick={() => navigate(action.url)}
      className={`w-full p-4 rounded border border-[#E2E8F0] text-left transition-all hover:shadow-md ${
        priorityStyles[action.priority] || priorityStyles.medium
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${priorityText[action.priority] || 'text-[#0A192F]'}`} />
          <div>
            <p className={`font-medium ${priorityText[action.priority] || 'text-[#0A192F]'}`}>{action.title}</p>
            <p className="text-sm text-[#475569]">{action.description}</p>
          </div>
        </div>
        <ArrowRight className={`w-5 h-5 ${priorityText[action.priority] || 'text-[#475569]'}`} />
      </div>
    </button>
  );
};

// Daily learning section with enterprise styling
const DailyLearning = ({ learning }) => {
  if (!learning?.has_data) return null;
  
  return (
    <div className="bg-white rounded border border-[#E2E8F0] p-6">
      <h3 className="text-lg font-semibold text-[#0A192F] mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-[#D4C19C]" />
        What We Learned Today
      </h3>
      
      {learning.accomplishments?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-[#0A192F] mb-2">Completed</h4>
          <ul className="space-y-1">
            {learning.accomplishments.map((item, idx) => (
              <li key={idx} className="text-sm text-emerald-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {learning.warnings?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-[#0A192F] mb-2">Needs Attention</h4>
          <ul className="space-y-1">
            {learning.warnings.map((item, idx) => (
              <li key={idx} className="text-sm text-amber-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {learning.improvements?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-[#0A192F] mb-2">Suggested Improvements</h4>
          <ul className="space-y-1">
            {learning.improvements.map((item, idx) => (
              <li key={idx} className="text-sm text-[#0A192F] flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-[#D4C19C]" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Main component
export default function ExecutiveBriefing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [askInput, setAskInput] = useState("");
  
  useEffect(() => {
    loadBriefing();
  }, []);
  
  const loadBriefing = async () => {
    setLoading(true);
    try {
      const res = await api.get("/operations/briefing");
      setBriefing(res.data);
    } catch (err) {
      console.error("Failed to load briefing:", err);
      toast.error("Failed to load briefing");
    } finally {
      setLoading(false);
    }
  };
  
  const handleAskAligned = () => {
    if (askInput.trim()) {
      // Navigate to Ask Leynix with the question
      navigate(`/ask-aligned?q=${encodeURIComponent(askInput)}`);
    } else {
      navigate('/ask-aligned');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A192F] mx-auto mb-4" />
          <p className="text-[#475569]">Loading Morning Briefing...</p>
        </div>
      </div>
    );
  }
  
  const { 
    greeting,
    metrics = {},
    operational_readiness = {},
    exceptions = {},
    suggested_actions = [],
    daily_learning = {},
    ask_aligned_prompt,
  } = briefing || {};
  
  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="executive-briefing">
      {/* Greeting Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F]">{greeting}</h1>
            <p className="text-[#475569]">Here&apos;s your operational overview</p>
          </div>
          <Button 
            variant="outline" 
            onClick={loadBriefing}
            className="border-[#E2E8F0] text-[#0A192F] hover:bg-[#F1F5F9] rounded"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Operational Readiness Banner */}
      <div className="bg-[#0A192F] rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between">
          <div className="text-white">
            <p className="text-[#D4C19C] font-medium tracking-wide uppercase text-xs mb-1">Operational Readiness</p>
            <p className="text-5xl font-bold">{operational_readiness.percentage}%</p>
            <Badge className={`mt-2 ${
              operational_readiness.is_ready 
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" 
                : "bg-amber-500/20 text-amber-300 border-amber-500/30"
            }`}>
              {operational_readiness.status}
            </Badge>
          </div>
          <div className="text-right text-white">
            {operational_readiness.remaining_items?.slice(0, 3).map((item, idx) => (
              <p key={idx} className="text-sm text-white/60 flex items-center justify-end gap-2">
                <Clock className="w-3 h-3" />
                {item}
              </p>
            ))}
            <Button 
              variant="outline" 
              className="mt-4 bg-white/10 border-white/30 text-white hover:bg-white/20 rounded"
              onClick={() => navigate('/validation')}
            >
              View Readiness Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-6 pt-4 border-t border-white/10">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#D4C19C] to-[#D4C19C]/70 rounded-full transition-all duration-500"
              style={{ width: `${operational_readiness.percentage}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Today's Operations Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Today&apos;s Operations</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(metrics).slice(0, 10).map(([key, metric]) => (
            <MetricCard 
              key={key} 
              metric={metric}
              onClick={() => {
                // Navigate based on metric type
                const routes = {
                  visits_scheduled: '/scheduling',
                  caregivers_working: '/employees',
                  coverage_needed: '/coverage',
                  compliance_alerts: '/compliance',
                  payroll_status: '/payroll',
                  billing_status: '/billing',
                };
                if (routes[key]) navigate(routes[key]);
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Exceptions Alert */}
      {exceptions.needs_attention > 0 && (
        <div 
          className="bg-red-50 border border-red-200 border-l-4 border-l-red-500 rounded p-4 mb-8 flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors"
          onClick={() => navigate('/exceptions')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-medium text-red-800">
                {exceptions.critical > 0 && `${exceptions.critical} Critical`}
                {exceptions.critical > 0 && exceptions.high > 0 && ' + '}
                {exceptions.high > 0 && `${exceptions.high} High Priority`}
                {' '}Operational Exception{exceptions.needs_attention !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-red-600">Click to view the Exception Center</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-red-600" />
        </div>
      )}
      
      {/* Two Column Layout: Actions + Learning */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Suggested Actions */}
        <div>
          <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Suggested Actions</h2>
          <div className="space-y-3">
            {suggested_actions.length > 0 ? (
              suggested_actions.map((action, idx) => (
                <ActionCard key={idx} action={action} />
              ))
            ) : (
              <div className="bg-emerald-50 rounded border border-emerald-200 p-6 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
                <p className="text-emerald-700 font-medium">All caught up!</p>
                <p className="text-emerald-600 text-sm">No urgent actions needed</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Daily Learning */}
        <div>
          <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Daily Learning</h2>
          {daily_learning?.has_data ? (
            <DailyLearning learning={daily_learning} />
          ) : (
            <div className="bg-[#F1F5F9] rounded border border-[#E2E8F0] p-6 text-center">
              <Clock className="w-12 h-12 mx-auto text-[#475569] mb-2" />
              <p className="text-[#475569]">Daily insights will appear here after operations begin</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Ask Leynix */}
      <div className="bg-[#0A192F] rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#D4C19C]/20 rounded flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#D4C19C]" />
          </div>
          <div className="text-white">
            <h2 className="font-semibold">Ask Leynix</h2>
            <p className="text-sm text-white/60">{ask_aligned_prompt}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Input
            value={askInput}
            onChange={(e) => setAskInput(e.target.value)}
            placeholder="Type your question or request..."
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-[#D4C19C]/30 rounded"
            onKeyDown={(e) => e.key === 'Enter' && handleAskAligned()}
          />
          <Button 
            onClick={handleAskAligned}
            className="bg-[#D4C19C] text-[#0A192F] hover:bg-[#D4C19C]/90 rounded"
          >
            <Send className="w-4 h-4 mr-2" />
            Ask
          </Button>
        </div>
      </div>
    </div>
  );
}
